import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCheckCircle,
  FaExchangeAlt,
  FaLink,
  FaMobileAlt,
  FaStore,
  FaSyncAlt,
  FaTrashAlt,
} from "react-icons/fa";
import {
  applyAccountLinkContext,
  canManageLinkedRestaurants,
  getAccountLinkContext,
  getStoredSessionSnapshot,
  hydrateAccountLinkContext,
  SESSION_UPDATED_EVENT,
  switchLinkedRestaurant,
} from "../utils/accountLinks";
import { normalizeRole } from "../utils/rbac";

const API_BASE = "https://qr.billsarthi.in/public/api";

const normalizeMobile = (value) => String(value || "").replace(/\D/g, "");

const LinkedRestaurantsPage = () => {
  const navigate = useNavigate();
  const [sessionUser, setSessionUser] = useState(() => {
    const session = getStoredSessionSnapshot();
    return session?.data || null;
  });
  const [context, setContext] = useState(() => getAccountLinkContext());
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [pendingMobile, setPendingMobile] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [step, setStep] = useState("enter");

  const syncFromStorage = () => {
    const session = getStoredSessionSnapshot();
    setSessionUser(session?.data || null);
    setContext(getAccountLinkContext(session));
  };

  useEffect(() => {
    syncFromStorage();

    const handleSessionUpdated = () => {
      syncFromStorage();
    };

    window.addEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
    return () => {
      window.removeEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
    };
  }, []);

  useEffect(() => {
    const run = async () => {
      const session = getStoredSessionSnapshot();
      const user = session?.data || null;

      if (!user?.id) {
        navigate("/login", { replace: true });
        return;
      }

      if (normalizeRole(user) !== "owner") {
        setLoading(false);
        return;
      }

      try {
        const nextContext = await hydrateAccountLinkContext({ force: true });
        if (nextContext) {
          setContext(nextContext);
        }
      } finally {
        syncFromStorage();
        setLoading(false);
      }
    };

    run();
  }, [navigate]);

  const currentRole = normalizeRole(sessionUser);
  const isOwner = currentRole === "owner";
  const activeRestaurantId = Number(sessionUser?.id || 0);
  const primaryRestaurantId = Number(context?.primary_restaurant_id || sessionUser?.id || 0);
  const canManage = isOwner && canManageLinkedRestaurants();
  const accounts = useMemo(() => context?.accounts || [], [context]);
  const primaryAccount = useMemo(
    () =>
      accounts.find((account) => Number(account?.id || 0) === primaryRestaurantId) ||
      sessionUser,
    [accounts, primaryRestaurantId, sessionUser]
  );

  const refreshList = async (force = true) => {
    if (!primaryRestaurantId) return;

    setListLoading(true);
    try {
      const response = await fetch(`${API_BASE}/restaurant/account-links/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: primaryRestaurantId }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) {
        throw new Error(json?.message || "Unable to refresh linked restaurants.");
      }

      const nextSession = applyAccountLinkContext(
        json?.account_link_context || json?.data?.account_link_context || null
      );
      setContext(getAccountLinkContext(nextSession));
      setSessionUser(nextSession?.data || sessionUser);
      if (force) {
        toast.success("Linked restaurants refreshed");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to refresh linked restaurants.");
    } finally {
      setListLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!canManage) {
      toast.error("Switch to the main restaurant to manage linked accounts.");
      return;
    }

    const normalizedMobile = normalizeMobile(mobileNumber);
    if (normalizedMobile.length < 10 || normalizedMobile.length > 15) {
      toast.error("Enter a valid mobile number.");
      return;
    }

    setSendingOtp(true);
    try {
      const response = await fetch(`${API_BASE}/restaurant/account-links/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: primaryRestaurantId,
          mobile_number: normalizedMobile,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) {
        throw new Error(json?.message || "Unable to send OTP.");
      }

      setPendingMobile(normalizedMobile);
      setStep("verify");
      setOtp("");
      toast.success(json?.message || "OTP sent successfully");
    } catch (error) {
      toast.error(error?.message || "Unable to send OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!canManage) {
      toast.error("Switch to the main restaurant to manage linked accounts.");
      return;
    }

    const nextOtp = normalizeMobile(otp);
    if (nextOtp.length < 4 || nextOtp.length > 6) {
      toast.error("Enter a valid OTP.");
      return;
    }

    setVerifyingOtp(true);
    try {
      const response = await fetch(`${API_BASE}/restaurant/account-links/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: primaryRestaurantId,
          mobile_number: pendingMobile,
          otp: nextOtp,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) {
        throw new Error(json?.message || "Unable to verify OTP.");
      }

      const nextSession = applyAccountLinkContext(
        json?.account_link_context || json?.data?.account_link_context || null
      );
      setContext(getAccountLinkContext(nextSession));
      setSessionUser(nextSession?.data || sessionUser);
      setStep("enter");
      setMobileNumber("");
      setPendingMobile("");
      setOtp("");
      toast.success(json?.message || "Restaurant linked successfully");
    } catch (error) {
      toast.error(error?.message || "Unable to verify OTP.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSwitch = (restaurantId) => {
    try {
      switchLinkedRestaurant(restaurantId);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error?.message || "Unable to switch restaurant.");
    }
  };

  const handleSwitchToPrimary = () => {
    if (!primaryRestaurantId || primaryRestaurantId === activeRestaurantId) return;
    handleSwitch(primaryRestaurantId);
  };

  const handleUnlink = async (linkedRestaurantId) => {
    if (!canManage) {
      toast.error("Only the main restaurant can remove linked accounts.");
      return;
    }

    const linkedAccount = accounts.find(
      (account) => Number(account?.id || 0) === Number(linkedRestaurantId || 0)
    );
    const label =
      linkedAccount?.restaurant_name ||
      linkedAccount?.name ||
      `Restaurant ${linkedRestaurantId}`;

    const confirmed = window.confirm(`Remove ${label} from linked restaurants?`);
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE}/restaurant/account-links/unlink`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: primaryRestaurantId,
          linked_restaurant_id: linkedRestaurantId,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) {
        throw new Error(json?.message || "Unable to unlink restaurant.");
      }

      const nextSession = applyAccountLinkContext(
        json?.account_link_context || json?.data?.account_link_context || null
      );
      setContext(getAccountLinkContext(nextSession));
      setSessionUser(nextSession?.data || sessionUser);
      toast.success(json?.message || "Linked restaurant removed successfully");
    } catch (error) {
      toast.error(error?.message || "Unable to unlink restaurant.");
    }
  };

  if (loading) {
    return (
      <Page>
        <ToastContainer position="top-right" autoClose={2200} />
        <EmptyCard>Loading linked restaurants...</EmptyCard>
      </Page>
    );
  }

  if (!isOwner) {
    return (
      <Page>
        <ToastContainer position="top-right" autoClose={2200} />
        <EmptyCard>
          This screen is available only for owner accounts.
        </EmptyCard>
      </Page>
    );
  }

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <HeaderCard>
        <HeaderMeta>
          <IconWrap>
            <FaLink />
          </IconWrap>
          <div>
            <Title>Linked Restaurants</Title>
            <Subtitle>
              Add another existing restaurant by OTP, then switch between linked owner accounts without logging out.
            </Subtitle>
          </div>
        </HeaderMeta>
        <HeaderActions>
          {activeRestaurantId !== primaryRestaurantId && (
            <SecondaryBtn type="button" onClick={handleSwitchToPrimary}>
              Back to main
            </SecondaryBtn>
          )}
          <PrimaryBtn
            type="button"
            onClick={() => refreshList(true)}
            disabled={listLoading}
          >
            <FaSyncAlt />
            <span>{listLoading ? "Refreshing..." : "Refresh"}</span>
          </PrimaryBtn>
        </HeaderActions>
      </HeaderCard>

      <Grid>
        <Panel>
          <PanelTitle>Main Account</PanelTitle>
          <SummaryCard>
            <SummaryLine>
              <strong>Restaurant</strong>
              <span>{primaryAccount?.restaurant_name || "NA"}</span>
            </SummaryLine>
            <SummaryLine>
              <strong>Owner</strong>
              <span>{primaryAccount?.name || "NA"}</span>
            </SummaryLine>
            <SummaryLine>
              <strong>Mobile</strong>
              <span>{primaryAccount?.mobile_number || "NA"}</span>
            </SummaryLine>
            <SummaryLine>
              <strong>Current session</strong>
              <span>
                {activeRestaurantId === primaryRestaurantId
                  ? "Main restaurant"
                  : sessionUser?.restaurant_name || sessionUser?.name || "Linked restaurant"}
              </span>
            </SummaryLine>
          </SummaryCard>

          {!canManage && (
            <NoticeBox>
              Management actions are locked while you are inside a linked restaurant session. Switch back to the main restaurant to add or remove accounts.
            </NoticeBox>
          )}

          <PanelTitle>Add Existing Restaurant</PanelTitle>
          <FieldLabel>Restaurant owner mobile number</FieldLabel>
          <InputRow>
            <FaMobileAlt />
            <input
              value={mobileNumber}
              onChange={(event) => setMobileNumber(event.target.value)}
              placeholder="Enter mobile number"
              inputMode="numeric"
              disabled={!canManage || sendingOtp}
            />
          </InputRow>
          <PrimaryBtn
            type="button"
            onClick={handleSendOtp}
            disabled={!canManage || sendingOtp}
          >
            <FaCheckCircle />
            <span>{sendingOtp ? "Sending OTP..." : "Send OTP"}</span>
          </PrimaryBtn>

          {step === "verify" && (
            <VerifyCard>
              <PanelTitle>Verify OTP</PanelTitle>
              <HintText>
                OTP has been sent to {pendingMobile || "the selected mobile number"}.
              </HintText>
              <InputRow>
                <FaCheckCircle />
                <input
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  placeholder="Enter OTP"
                  inputMode="numeric"
                  disabled={!canManage || verifyingOtp}
                />
              </InputRow>
              <ActionRow>
                <SecondaryBtn
                  type="button"
                  onClick={() => {
                    setStep("enter");
                    setOtp("");
                    setPendingMobile("");
                  }}
                  disabled={verifyingOtp}
                >
                  Cancel
                </SecondaryBtn>
                <PrimaryBtn
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={!canManage || verifyingOtp}
                >
                  <FaCheckCircle />
                  <span>{verifyingOtp ? "Verifying..." : "Verify & Link"}</span>
                </PrimaryBtn>
              </ActionRow>
            </VerifyCard>
          )}
        </Panel>

        <Panel>
          <PanelTitle>Available Accounts</PanelTitle>
          <HintText>
            The current session can switch among the main restaurant and all linked owner accounts.
          </HintText>

          <ListWrap>
            {accounts.length ? (
              accounts.map((account) => {
                const accountId = Number(account?.id || 0);
                const isPrimary = accountId === primaryRestaurantId;
                const isActive = accountId === activeRestaurantId;
                return (
                  <AccountCard key={accountId}>
                    <AccountTop>
                      <AccountInfo>
                        <AccountName>
                          <FaStore />
                          <span>{account?.restaurant_name || `Restaurant ${accountId}`}</span>
                        </AccountName>
                        <AccountMeta>{account?.name || "Owner"}</AccountMeta>
                        <AccountMeta>{account?.mobile_number || "NA"}</AccountMeta>
                      </AccountInfo>
                      <BadgeRow>
                        {isPrimary && <Badge $accent="blue">Main</Badge>}
                        {isActive && <Badge $accent="green">Active</Badge>}
                      </BadgeRow>
                    </AccountTop>

                    <CardActions>
                      {!isActive ? (
                        <PrimaryBtn
                          type="button"
                          onClick={() => handleSwitch(accountId)}
                        >
                          <FaExchangeAlt />
                          <span>Switch</span>
                        </PrimaryBtn>
                      ) : (
                        <SecondaryBtn type="button" disabled>
                          Current
                        </SecondaryBtn>
                      )}

                      {!isPrimary && (
                        <DangerBtn
                          type="button"
                          onClick={() => handleUnlink(accountId)}
                          disabled={!canManage}
                          title={
                            canManage
                              ? "Remove linked restaurant"
                              : "Switch to the main restaurant to unlink"
                          }
                        >
                          <FaTrashAlt />
                          <span>Unlink</span>
                        </DangerBtn>
                      )}
                    </CardActions>
                  </AccountCard>
                );
              })
            ) : (
              <EmptyState>No linked restaurants found yet.</EmptyState>
            )}
          </ListWrap>
        </Panel>
      </Grid>
    </Page>
  );
};

export default LinkedRestaurantsPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 20px;
  background: #f3f6fb;
  display: grid;
  gap: 16px;
`;

const HeaderCard = styled.div`
  background: linear-gradient(135deg, #0a4d82, #0b6aa9);
  color: #fff;
  border-radius: 18px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const HeaderMeta = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.16);
  display: grid;
  place-items: center;
  font-size: 20px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  max-width: 760px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.88);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
`;

const Panel = styled.section`
  background: #fff;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);
  display: grid;
  gap: 14px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 800;
`;

const SummaryCard = styled.div`
  border: 1px solid #dbe7f3;
  border-radius: 14px;
  background: #f8fbff;
  padding: 14px;
  display: grid;
  gap: 10px;
`;

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.95rem;
  color: #1e293b;

  strong {
    color: #475569;
  }

  span {
    text-align: right;
  }
`;

const NoticeBox = styled.div`
  border-radius: 14px;
  padding: 12px 14px;
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
  font-size: 0.94rem;
  line-height: 1.45;
`;

const FieldLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 700;
  color: #334155;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;

  input {
    flex: 1;
    border: 0;
    outline: 0;
    font-size: 0.96rem;
    background: transparent;
    color: inherit;
  }
`;

const HintText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.45;
`;

const VerifyCard = styled.div`
  border: 1px dashed #bfd8ec;
  border-radius: 16px;
  background: #f8fbff;
  padding: 14px;
  display: grid;
  gap: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ListWrap = styled.div`
  display: grid;
  gap: 12px;
`;

const AccountCard = styled.div`
  border: 1px solid #dbe7f3;
  border-radius: 16px;
  background: #fff;
  padding: 14px;
  display: grid;
  gap: 14px;
`;

const AccountTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
`;

const AccountInfo = styled.div`
  display: grid;
  gap: 6px;
`;

const AccountName = styled.div`
  display: inline-flex;
  gap: 8px;
  align-items: center;
  font-weight: 800;
  color: #0f172a;
`;

const AccountMeta = styled.div`
  color: #64748b;
  font-size: 0.92rem;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  background: ${({ $accent }) =>
    $accent === "green" ? "#dcfce7" : "#dbeafe"};
  color: ${({ $accent }) =>
    $accent === "green" ? "#166534" : "#1d4ed8"};
`;

const CardActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const buttonBase = `
  border: 0;
  border-radius: 12px;
  padding: 11px 14px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: transform 0.12s ease, opacity 0.12s ease;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const PrimaryBtn = styled.button`
  ${buttonBase}
  background: linear-gradient(135deg, #0a6db0, #015b9a);
  color: #fff;
`;

const SecondaryBtn = styled.button`
  ${buttonBase}
  background: #e2e8f0;
  color: #0f172a;
`;

const DangerBtn = styled.button`
  ${buttonBase}
  background: #fee2e2;
  color: #b91c1c;
`;

const EmptyCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  color: #475569;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);
`;

const EmptyState = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  color: #64748b;
`;
